//#region src/payors/registry.ts
/**
* Canonical payor identity, the folding rules that reach it, and the names we
* use in front of people.
*
* Lives in `@repo/config` rather than `@repo/services` because the tenant clinic
* configs derive their payor maps from it and config cannot import services. A
* leaf: no `@repo/db`, so `apps/web` can import it.
*/
/**
* What a provider office calls a payor, as opposed to the code we store.
* Many-to-one: BCBSTX, BCBSAZ and Blue Cross Medicare Advantage are all "Blue
* Cross" to a receptionist. Read aloud on calls and stored in
* `organizations.insuranceGroupsAccepted`.
*
* A tuple because it types the LLM extraction enum, which needs a literal union.
*/
const COLLOQUIAL_PAYOR_NAMES = [
	"Aetna",
	"Arizona Complete Health",
	"Blue Cross",
	"Cigna",
	"Demo Health",
	"Humana",
	"Medicare",
	"Superior Health Plan",
	"United Healthcare",
	"WellMed",
	"Wellpoint"
];
/**
* Every payor we recognise. `CANONICAL_PAYORS` and the alias index are built
* from it, so a payor is added here and nowhere else.
*
* Two foldings are deliberately absent, because either would file a prior auth
* against the wrong plan: "AARP BY UHC" is a distinct Medicare product despite
* containing "UHC", and bare "Humana" never folds into "Humana Insurance
* Company", since Humana files under several legal entities. Anything unlisted
* passes through and fails the supported-payor checks, which is the safe
* direction — a refusal is recoverable, a misfiled prior auth is not.
*/
const PAYOR_TABLE = {
	Aetna: {
		label: "Aetna",
		colloquialName: "Aetna",
		payorId: "AETNA",
		spellings: ["Aetna Health Plans"]
	},
	AZCompleteHealth: {
		label: "Arizona Complete Health",
		colloquialName: "Arizona Complete Health",
		payorId: "68069"
	},
	BCBSAZ: {
		label: "AZ Blue (BCBSAZ)",
		colloquialName: "Blue Cross",
		payorId: "53589"
	},
	BCBSTX: {
		label: "Blue Cross Blue Shield of Texas (BCBSTX)",
		colloquialName: "Blue Cross",
		payorId: "00021",
		spellings: [
			"BCBS-TX",
			"BCBS of TX",
			"Blue Cross and Blue Shield of Texas"
		]
	},
	"Blue Cross Medicare Advantage": {
		label: "Blue Cross Medicare Advantage",
		colloquialName: "Blue Cross",
		payorId: "66006",
		spellings: [
			"Blue Cross Blue Shield Medicare Advantage",
			"Blue Cross Blue Shield Medicare Advantage MEDICARE ADVANTAGE PPO",
			"Blue Cross Blue Shield Medicare Advantag"
		]
	},
	Cigna: {
		label: "Cigna",
		colloquialName: "Cigna"
	},
	DemoHealth: {
		label: "Demo Health",
		colloquialName: "Demo Health"
	},
	HealthSpring: {
		label: "HealthSpring (Cigna)",
		colloquialName: "Cigna",
		payorId: "52192"
	},
	Humana: {
		label: "Humana",
		colloquialName: "Humana",
		payorId: "61101"
	},
	"Humana Insurance Company": {
		label: "Humana Insurance Company",
		colloquialName: "Humana"
	},
	Medicare: {
		label: "Medicare",
		colloquialName: "Medicare",
		payorId: "04412",
		spellings: ["Novitas Solutions Part A"]
	},
	"Superior Health Plan": {
		label: "Superior Health Plan",
		colloquialName: "Superior Health Plan",
		spellings: ["Superior Health Plan Texas"]
	},
	UHC: {
		label: "United Healthcare (UHC)",
		colloquialName: "United Healthcare",
		payorId: "87726",
		spellings: [
			"United Healthcare",
			"UnitedHealthcare",
			"United Health Care",
			"United HealthCare of all states"
		]
	},
	WellMed: {
		label: "WellMed",
		colloquialName: "WellMed"
	},
	Wellpoint: {
		label: "Wellpoint",
		colloquialName: "Wellpoint",
		payorId: "WLPNT",
		spellings: ["Wellpoint HMO"]
	}
};
const CANONICAL_PAYORS = Object.keys(PAYOR_TABLE);
/** The table under one value type; `as const` above types each entry literally. */
const PAYORS = PAYOR_TABLE;
/** The whole tolerance of the matching. Everything else must match exactly. */
function collapseWhitespace(payor) {
	return payor.trim().replace(/\s+/g, " ");
}
function payorLookupKey(payor) {
	return collapseWhitespace(payor).toLowerCase();
}
/**
* Flat index so `normalizePayor` is one map read. A spelling claimed by two
* payors would resolve to whichever was indexed last; `payors.test.ts` rejects
* that rather than this build order defending against it.
*/
const PAYOR_ALIASES = (() => {
	const index = /* @__PURE__ */ new Map();
	for (const payor of CANONICAL_PAYORS) {
		index.set(payorLookupKey(payor), payor);
		for (const spelling of PAYORS[payor].spellings ?? []) index.set(payorLookupKey(spelling), payor);
	}
	return index;
})();
function spellingsFor(payor) {
	return [payor, ...PAYORS[payor].spellings ?? []];
}
/**
* Folds a payor to its canonical spelling, or returns it trimmed and otherwise
* unchanged. Call this before matching against any mapping table or supported
* list, and hand the result downstream so everything sees one spelling.
*/
function normalizePayor(payor) {
	const collapsed = collapseWhitespace(payor);
	return PAYOR_ALIASES.get(collapsed.toLowerCase()) ?? collapsed;
}
/** `hasOwnProperty`, not `in`, which would make "toString" a payor. */
function isCanonicalPayor(payor) {
	return Object.prototype.hasOwnProperty.call(PAYORS, payor);
}
/**
* Resolved for display only. "BCBS" is ambiguous between three payors so it must
* never fold, but it has one unambiguous colloquial form. Bare "Blue Cross"
* needs no entry — falling through already returns it.
*/
const DISPLAY_ONLY_COLLOQUIAL = { BCBS: "Blue Cross" };
function colloquialPayorName(payor) {
	const canonical = normalizePayor(payor);
	if (isCanonicalPayor(canonical)) return PAYORS[canonical].colloquialName;
	return DISPLAY_ONLY_COLLOQUIAL[canonical] ?? canonical;
}
/** The plan shapes each payor sells. Irregular because the products are. */
const PAYOR_PRODUCTS = {
	Aetna: [
		"HMO",
		"PPO",
		"Medicare Advantage HMO",
		"Medicare Advantage PPO"
	],
	"Arizona Complete Health": ["HMO", "PPO"],
	"Blue Cross": [
		"HMO",
		"PPO",
		"Medicare Advantage HMO",
		"Medicare Advantage PPO"
	],
	Cigna: [
		"HMO",
		"PPO",
		"Medicare Advantage HMO",
		"Medicare Advantage PPO"
	],
	"Demo Health": ["HMO", "PPO"],
	Humana: [
		"HMO",
		"PPO",
		"Medicare Advantage HMO",
		"Medicare Advantage PPO"
	],
	Medicare: ["Part A", "Part B"],
	"Superior Health Plan": [""],
	"United Healthcare": [
		"HMO",
		"PPO",
		"Medicare Advantage HMO",
		"Medicare Advantage PPO"
	],
	WellMed: ["Medicare Advantage HMO", "Medicare Advantage PPO"],
	Wellpoint: ["HMO", "PPO"]
};
/**
* Stored in `organizations.insuranceGroupsAccepted`, so this set must only ever
* grow — dropping a name orphans the rows holding it.
*/
const COLLOQUIAL_INSURANCE_NAMES = COLLOQUIAL_PAYOR_NAMES.flatMap((payor) => PAYOR_PRODUCTS[payor].map((product) => product ? `${payor} ${product}` : payor));

//#endregion
export { CANONICAL_PAYORS, COLLOQUIAL_INSURANCE_NAMES, COLLOQUIAL_PAYOR_NAMES, PAYORS, colloquialPayorName, isCanonicalPayor, normalizePayor, spellingsFor };